import { Box, useTheme } from '@mui/material';
import { BRAND_MARKS, BRAND_FALLBACK_COLOR } from './brandMarks.js';

/**
 * The vendor's mark for an integration, drawn from a vendored CC0 path where
 * one exists and a monogram in the brand's colour where it does not.
 *
 * Brand colours are chosen for a white page, so a dark one (GitHub's near-black
 * at #181717) disappears against the dark theme. Rather than picking a second
 * palette by hand, the colour is lifted until it reads — the mark stays
 * recognisably the brand's without becoming invisible.
 */
export function BrandIcon({ id, name, size = 28 }: { id: string; name: string; size?: number }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const mark = BRAND_MARKS[id];

  if (mark) {
    return (
      <Box
        component="svg"
        viewBox="0 0 24 24"
        role="img"
        aria-label={name}
        // A wordmark keeps its height and takes the width it needs; a glyph
        // stays square. Fitting a logotype into a square is what turns it into
        // an unreadable smudge.
        sx={{
          width: mark.wide ? size * 2.6 : size,
          height: size,
          display: 'block',
          flexShrink: 0,
        }}
      >
        <title>{name}</title>
        <path d={mark.path} fill={readable(mark.hex, dark)} />
      </Box>
    );
  }

  const color = readable(BRAND_FALLBACK_COLOR[id] ?? theme.palette.text.secondary, dark);
  return (
    <Box
      aria-label={name}
      role="img"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '22%',
        display: 'grid',
        placeItems: 'center',
        color,
        // A tint of the brand colour, so the tile belongs to the same family as
        // the real marks beside it instead of reading as a missing image.
        bgcolor: alpha(color, dark ? 0.18 : 0.12),
        fontSize: size * 0.5,
        fontWeight: 700,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {monogram(name)}
    </Box>
  );
}

/** First letters of the first two words: "Microsoft Teams" → MT, "Slack" → S. */
function monogram(name: string): string {
  const words = name.replace(/[^\p{L}\p{N} ]/gu, ' ').split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return (words[0]?.[0] ?? '?').toUpperCase();
}

/**
 * Lifts a colour that would be lost against the dark theme's background,
 * keeping its hue. Colours that already read are returned untouched.
 */
function readable(hex: string, dark: boolean): string {
  if (!dark) return hex;
  const rgb = parse(hex);
  if (!rgb) return hex;
  // Perceived brightness — the green channel dominates what the eye reads.
  const lum = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  if (lum > 0.35) return hex;
  const lift = 0.35 / Math.max(lum, 0.04);
  const capped = rgb.map((c) => Math.min(255, Math.round(Math.max(c, 24) * lift)));
  return `rgb(${capped.join(',')})`;
}

function parse(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function alpha(color: string, a: number): string {
  const rgb = parse(color);
  if (rgb) return `rgba(${rgb.join(',')},${a})`;
  const m = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(color);
  return m ? `rgba(${m[1]},${m[2]},${m[3]},${a})` : color;
}
